
import Vue, { PropType } from 'vue';

// Types
import { CandidateJobBuilder } from '~/utils/candidate';
import {
    Sector,
    CareerField,
    Employer,
    CandidateSector,
    CandidateCareerField,
} from '~/services/types/candidate';
import CandidateService from '~/services/CandidateService';

// Components
import InfoBox from '../components/InfoBox.vue';
import Select, { SelectOption } from '~/components/builders/Base/Select.vue';
import IconBtn from '~/components/builders/IconBtn.vue';

export default Vue.extend({
    name: 'JobPreferencesForm',
    components: { InfoBox, Select, IconBtn },
    props: {
        builder: {
            type: Object as PropType<CandidateJobBuilder>,
            required: true,
        },
        loading: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            sectors: this.builder.getPreferencesField('sectors') || ([] as CandidateSector[]),
            careerFields:
                this.builder.getPreferencesField('careerFields') || ([] as CandidateCareerField[]),
            employers: this.builder.getPreferencesField('preferredEmployers'),

            candidateService: new CandidateService(),

            selectedSector: '',
            selectedCareerField: '',
            selectedEmployers: this.builder.getPreferencesField('preferredEmployers'),
            sectorOptions: [] as SelectOption<string>[],
            careerFieldOptions: [] as SelectOption<string>[],
            employerOptions: [] as SelectOption<string>[],

            isLoading: false,
        };
    },
    mounted() {
        this.$watch('sectors', this.builder.watchPreference('sectors'));
        this.$watch('careerFields', this.builder.watchPreference('careerFields'));
        this.$watch('employers', this.builder.watchPreference('preferredEmployers'));

        this.load();
    },
    computed: {
        renderedSectors(): { id: string; label: string }[] {
            return this.sectors.map(sector => {
                const sectorOption = this.sectorOptions.find(
                    option => option.value === sector.sectorId
                );
                return {
                    id: sector.sectorId,
                    label: sectorOption ? sectorOption.label : sector.sectorId,
                };
            });
        },
        renderedCareerFields(): { id: string; label: string }[] {
            return this.careerFields.map(candidateCF => {
                const careerFieldOption = this.careerFieldOptions.find(
                    option => option.value === candidateCF.careerFieldId
                );
                return {
                    id: candidateCF.careerFieldId,
                    label: careerFieldOption ? careerFieldOption.label : candidateCF.careerFieldId,
                };
            });
        },
    },
    methods: {
        handleAddSector(sectorId: string) {
            console.log('SectorID:', sectorId);
            if (this.sectors.length >= 3) return;

            if (this.sectors.findIndex(s => s.sectorId === sectorId) !== -1) return;

            this.sectors.push({
                id: CandidateJobBuilder.generateId(),
                sectorId,
                candidateId: '',
                createdAt: new Date(),
                updatedAt: new Date(),
            });
        },
        handleRemoveSector(sectorId: string) {
            this.sectors = this.sectors.filter(sector => sector.sectorId !== sectorId);
        },
        handleAddCareerField(careerFieldId: string) {
            if (this.careerFields.length >= 3) return;
            if (this.careerFields.findIndex(c => c.careerFieldId == careerFieldId) !== -1) return;
            this.careerFields.push({
                id: CandidateJobBuilder.generateId(),
                careerFieldId,
                candidateId: '',
                createdAt: new Date(),
                updatedAt: new Date(),
            });
        },
        handleRemoveCareerField(careerFieldId: string) {
            this.careerFields = this.careerFields.filter(
                careerField => careerField.careerFieldId !== careerFieldId
            );
        },
        handleAddEmployer(idx: number, employerId: string) {
            // NB! Here employerId can be a custom employer name.
            employerId = employerId.trim();

            // Do not add if the employer is already selected
            if (this.selectedEmployers.includes(employerId)) return;

            // Check if this is a custom employer
            let employerOption = this.employerOptions.find(option => option.value === employerId);
            if (!employerOption) {
                // This is a custom employer, but double check that it is not already in the list
                employerOption = this.employerOptions.find(
                    option =>
                        option.label.toLocaleLowerCase() === employerId.trim().toLocaleLowerCase()
                );

                if (!employerOption) {
                    // Add the custom employer to the list of options
                    const label = employerId;
                    employerId = CandidateJobBuilder.generateIdWithValues([label, idx.toString()]);
                    this.employerOptions.push({
                        value: employerId,
                        label: label,
                    });
                } else {
                    employerId = employerOption.value;

                    // Do not add if the employer is already selected
                    if (this.selectedEmployers.includes(employerId)) return;
                }
            }

            this.$set(this.selectedEmployers, idx, employerId);
        },
        handleRemoveEmployer(idx: number) {
            this.$set(this.selectedEmployers, idx, '');
        },
        async load() {
            this.isLoading = true;
            Promise.all([this.loadSectors(), this.loadCareerFields(), this.loadEmployers()])
                .then(([sectors, careerFields, employers]) => {
                    this.sectorOptions = sectors.map(sector => ({
                        value: sector.id,
                        label: sector.name,
                    }));
                    this.sectorOptions.sort((a, b) => a.label.localeCompare(b.label));
                    this.careerFieldOptions = careerFields.map(careerField => ({
                        value: careerField.id,
                        label: careerField.name,
                    }));
                    this.careerFieldOptions.sort((a, b) => a.label.localeCompare(b.label));
                    this.employerOptions = employers.map(employer => ({
                        value: employer.id,
                        label: employer.name,
                    }));
                    this.employerOptions.sort((a, b) => a.label.localeCompare(b.label));
                })
                .then(() => {
                    this.isLoading = false;
                });
        },
        async loadSectors(): Promise<Sector[]> {
            return this.candidateService.getAllSectors();
        },
        async loadCareerFields(): Promise<CareerField[]> {
            return this.candidateService.getAllCareerFields();
        },
        async loadEmployers(): Promise<Employer[]> {
            return this.candidateService.getAllEmployers(this.builder.candidateId);
        },
    },
    watch: {
        selectedEmployers(employers: string[]) {
            const filteredEmployers: string[] = employers.filter((employer: string) => employer);

            // Remove duplicates
            this.employers = [...new Set(filteredEmployers)];
        },
    },
});
